/**
 * Masifi additions to the upstream sidebar (Sidebar.vue only imports and
 * calls this; see docs/core-touchpoints-slim-design.md §4.2):
 * - Leads + Agenda after the top-level "Inbox" item, "Masifi Inbox" under
 *   Settings after "Account Settings". Items are inserted by anchor NAME, so a
 *   rebase that renames an anchor logs loudly (and the spec fails) instead of
 *   the Masifi item vanishing silently.
 * - The configured home view (ui_settings.default_home_view) goes first.
 * - Agenda stats on mount, gated by the CRM module (feature_crm). The items
 *   themselves are hidden by the native <Policy feature-flag> via the route's
 *   meta.featureFlag.
 * - Mobile: close the drawer on every navigation (closeMobileSidebar is a
 *   no-op on desktop / when already closed).
 * - "Control Panel" group right before Settings, only for users with a
 *   Control Panel scope (useControlPanelAccess; one /console/me per session,
 *   requested only for SuperAdmins).
 */
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { masifiHomeView } from 'dashboard/helper/masifi/masifiHomeView';
import { useControlPanelAccess } from 'dashboard/composables/masifi/useControlPanelAccess';

export const MASIFI_SIDEBAR_ANCHORS = Object.freeze({
  top: 'Inbox',
  settings: 'Settings',
  settingsAfter: 'Settings Account Settings',
});

const reportMissingAnchor = anchor => {
  // eslint-disable-next-line no-console
  console.error(`[masifi-sidebar] anchor missing: ${anchor}`);
};

// Returns a new array with `additions` right after the item named `anchor`
// (or the same items plus a loud error when the anchor is gone).
const insertAfter = (items, anchor, additions) => {
  const index = items.findIndex(item => item.name === anchor);
  if (index === -1) {
    reportMissingAnchor(anchor);
    return items;
  }
  return [
    ...items.slice(0, index + 1),
    ...additions,
    ...items.slice(index + 1),
  ];
};

// Returns a new array with `additions` right before the item named `anchor`.
const insertBefore = (items, anchor, additions) => {
  if (!additions.length) return items;
  const index = items.findIndex(item => item.name === anchor);
  if (index === -1) {
    reportMissingAnchor(anchor);
    return items;
  }
  return [...items.slice(0, index), ...additions, ...items.slice(index)];
};

// Name of the menu item that goes first for the user's home view, derived
// from the shared masifiHomeView rule (same one the router and login use).
const homeItemName = uiSettings =>
  masifiHomeView({ ui_settings: uiSettings }) === 'dashboard'
    ? 'Conversation'
    : 'Leads';

const moveToFront = (items, name) => {
  const index = items.findIndex(item => item.name === name);
  if (index <= 0) return items;
  return [items[index], ...items.slice(0, index), ...items.slice(index + 1)];
};

export function useMasifiSidebar(menuItems, closeMobileSidebar) {
  const { t } = useI18n();
  const { accountScopedRoute } = useAccount();
  const store = useStore();
  const route = useRoute();
  const { uiSettings } = useUISettings();
  const accountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const { access: controlPanelAccess, load: loadControlPanel } =
    useControlPanelAccess();

  onMounted(() => {
    // Only asks /console/me when the user is a SuperAdmin (no request otherwise).
    loadControlPanel(store.getters.getCurrentUser);
    const userId = store.getters.getCurrentUser?.id;
    if (
      userId &&
      isFeatureEnabledonAccount.value(accountId.value, 'feature_crm')
    ) {
      store.dispatch('agenda/fetchStats', { assignee_id: userId });
    }
  });

  watch(
    () => route.fullPath,
    () => closeMobileSidebar()
  );

  const topItems = () => [
    {
      name: 'Leads',
      label: t('SIDEBAR.LEADS'),
      icon: 'i-lucide-handshake',
      to: accountScopedRoute('leads_dashboard_index'),
      activeOn: ['leads_dashboard_index'],
    },
    {
      name: 'Agenda',
      label: t('SIDEBAR.AGENDA'),
      icon: 'i-lucide-calendar-clock',
      to: accountScopedRoute('tasks_agenda'),
      activeOn: ['tasks_agenda'],
      getterKeys: {
        count: 'agenda/getAgendaDueCount',
      },
    },
  ];

  const settingsItems = () => [
    {
      name: 'Settings Masifi Inbox',
      label: t('SIDEBAR.MASIFI_INBOX'),
      icon: 'i-lucide-sliders-horizontal',
      to: accountScopedRoute('masifi_settings_index'),
    },
  ];

  const controlPanelItems = () =>
    controlPanelAccess.value?.scope
      ? [
          {
            name: 'Control Panel',
            label: t('SIDEBAR.CONTROL_PANEL'),
            icon: 'i-lucide-gauge',
            children: [
              {
                name: 'Control Panel Summary',
                label: t('CONTROL_PANEL.NAV.SUMMARY'),
                to: accountScopedRoute('control_panel_summary'),
                activeOn: ['control_panel_summary'],
              },
              {
                name: 'Control Panel Accounts',
                label: t('CONTROL_PANEL.NAV.ACCOUNTS'),
                to: accountScopedRoute('control_panel_accounts'),
                activeOn: ['control_panel_accounts'],
              },
              // Cloudflare: solo la raíz (SuperAdmin en el maestro); una agencia (F3) no lo ve.
              ...(controlPanelAccess.value?.scope === 'root'
                ? [
                    {
                      name: 'Control Panel Cloudflare',
                      label: t('CONTROL_PANEL.NAV.CLOUDFLARE'),
                      to: accountScopedRoute('control_panel_cloudflare'),
                      activeOn: ['control_panel_cloudflare'],
                    },
                  ]
                : []),
              {
                name: 'Control Panel Activity',
                label: t('CONTROL_PANEL.NAV.ACTIVITY'),
                to: accountScopedRoute('control_panel_activity'),
                activeOn: ['control_panel_activity'],
              },
            ],
          },
        ]
      : [];

  const withSettingsItems = items => {
    const { settings, settingsAfter } = MASIFI_SIDEBAR_ANCHORS;
    const index = items.findIndex(item => item.name === settings);
    if (index === -1) {
      reportMissingAnchor(settings);
      return items;
    }
    const settingsItem = items[index];
    const children = insertAfter(
      settingsItem.children || [],
      settingsAfter,
      settingsItems()
    );
    return items.map((item, i) =>
      i === index ? { ...settingsItem, children } : item
    );
  };

  return computed(() => {
    const items = insertAfter(
      menuItems.value,
      MASIFI_SIDEBAR_ANCHORS.top,
      topItems()
    );
    return moveToFront(
      insertBefore(
        withSettingsItems(items),
        MASIFI_SIDEBAR_ANCHORS.settings,
        controlPanelItems()
      ),
      homeItemName(uiSettings.value)
    );
  });
}
